import Vue from 'vue';
import Vuex, { MutationTree, ActionTree } from 'vuex';
import axios from 'axios';

import type { CompanyData } from '@/models/companyData';
import type { Company } from '@/models/company';
import type { CompanyUser } from '@/models/companyUser';
import type { CompanySetting } from '@/models/companySetting';
import type { DynamicField } from '@/models/dynamicField';
import { dynamicFieldWebservice } from '@/webservices/dynamicField';
import debounce from 'lodash/debounce'; // Import debounce function from lodash

Vue.use(Vuex);

interface State {
    companies: CompanyData[];
    users: CompanyUser[];
    selectedCompany: Company; // the company that Master have selected
    dynamicFields: DynamicField[];
}

const mutations: MutationTree<State> = {
    setCompanies(currentState, companies: CompanyData[]) {
        currentState.companies = companies;
    },

    setUsers(currentState, users: CompanyUser[]) {
        currentState.users = users;
    },

    setSelectedCompany(currentState, company: Company) {
        currentState.selectedCompany = company;
    },

    setDynamicFields(currentState, dynamicFields: DynamicField[]) {
        currentState.dynamicFields = dynamicFields;
    },

    resetState() {
        // console.log('resetState FIRED FROM Account STORE');
        // acquire initial state
        const s = initialState();

        Object.keys(s).forEach((key) => {
            state[key] = s[key];
        });
    },
};

const actions: ActionTree<State, any> = {
    fetchAllCompanies({ commit }): any {
        axios.get('/companies').then(
            (response) => {
                const companies: CompanyData[] = response && response.data;
                commit('setCompanies', companies);
            },
            (error) => {
                console.log(error);
            }
        );
    },
    fetchAllDynamicFields: debounce(function ({ commit }) {
        dynamicFieldWebservice.load().then((response) => {
            const dynamic_fields: DynamicField[] = response && response.data;
            dynamic_fields.sort((a: DynamicField, b: DynamicField) => {
                if (!a.value || !b.value) {
                    return 0;
                }
                return a.value === b.value ? 0 : a.value < b.value ? 1 : -1;
            });
            commit('setDynamicFields', dynamic_fields);
        });
    }, 500), // milliseconds
};

const getters = {
    companies(currentState: State): CompanyData[] {
        return currentState.companies;
    },
    users(currentState: State): CompanyUser[] {
        return currentState.users;
    },
    dynamicFields(currentState: State): DynamicField[] {
        return currentState.dynamicFields;
    },
    selectedCompany(currentState: State): Company {
        return currentState.selectedCompany;
    },
};

function initialState() {
    return {
        companies: Array<CompanyData>(),
        users: Array<CompanyUser>(),
        selectedCompany: {
            id: 0,
            name: '',
            description: '',
            email: '',
            token: '',
            phone: '',
            address: '',
            address_addition: '',
            postcode: '',
            city: '',
            country: '',
            admin_id: 0,
            clonedData: false,
            logo_path: '',
            can_use_ms_graph: false,
            setting: {
                id: 0,
                feature_tags: false,
                feature_integrations: false,
            } as CompanySetting,
        },
        dynamicFields: Array<DynamicField>(),
    };
}

const state: State = initialState();

const module = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default module;
